import { useGuardedDefaultValues } from "@msuite/ui/hooks";
import { resolver } from "@msuite/ui/libs/form";
import { useTranslation } from "@msuite/ui/localization";
import { Modal, type ModalProps } from "@msuite/ui/modal";
import { invalidate, useQueryClient } from "@msuite/ui/query";
import { responseHandler } from "@msuite/ui/utils";
import {
	type IncomingInvoiceDefaultApproversSchema,
	incomingInvoiceDefaultApproversSchema,
} from "@msuite/utils/schemas";
import { guardString } from "@msuite/utils/strings";
import { FormProvider, useForm } from "react-hook-form";
import { ModalLoadFailed } from "@/components/modals";
import { IncomingInvoiceDefaultApproversForm } from "@/forms/incoming-invoice-default-approvers-form";
import { client } from "@/hono";
import { useAuth } from "@/hooks/use-auth";

/** Props Interface */
interface EditDefaultApproversModalProps extends ModalProps {
	/** The workspace whose set is edited, or null for the organisation's. */
	workspaceId: string | null;
	/** What the set belongs to, for the title. */
	name: string;
}

export function EditDefaultApproversModal({
	isOpen,
	onClose,
	workspaceId,
	name,
}: EditDefaultApproversModalProps) {
	/** Hooks */
	const { t } = useTranslation();
	const { organization } = useAuth();
	const qc = useQueryClient();
	const { defaultValues, loadFailed } = useGuardedDefaultValues(async () => {
		const response = await client.incoming_invoices["default-approvers"].$get();
		const data = await responseHandler(response);
		const userIds = data.defaultApprovers
			.filter((approver) => approver.workspaceId === workspaceId)
			.map((approver) => approver.user.id);
		// An organisation that never chose its set is approved by its owner, so the form opens with
		// them rather than empty.
		if (!workspaceId && !userIds.length) userIds.push(...(await getOwnerIds()));
		return { workspaceId, userIds } satisfies IncomingInvoiceDefaultApproversSchema;
	});
	const methods = useForm<IncomingInvoiceDefaultApproversSchema>({
		defaultValues,
		resolver: resolver(incomingInvoiceDefaultApproversSchema),
	});

	/** Functions */
	async function getOwnerIds() {
		const response = await client.organizations[":orgId"].members.$get({
			param: { orgId: guardString(organization?.orgId) },
			query: {},
		});
		const data = await responseHandler(response);
		return data.members
			.filter((member) => member.membership.role === "owner")
			.map((member) => member.user.id);
	}

	async function handleOnSubmit(json: IncomingInvoiceDefaultApproversSchema) {
		const response = await client.incoming_invoices["default-approvers"].$patch({ json });
		await responseHandler(response);
		await qc.invalidateQueries({ predicate: invalidate(["incoming-invoice-default-approvers"]) });
		onClose();
	}

	/** Render */
	return (
		<FormProvider {...methods}>
			<Modal
				isOpen={isOpen}
				onClose={onClose}
				title={t("incoming_invoices.edit_default_approvers", { name })}
				onSubmit={methods.handleSubmit(handleOnSubmit)}
				isLoading={methods.formState.isLoading && !loadFailed}
			>
				{loadFailed ? (
					<ModalLoadFailed onClose={onClose} />
				) : (
					<>
						<Modal.Body>
							<IncomingInvoiceDefaultApproversForm isOrganizationSet={workspaceId === null} />
						</Modal.Body>
						<Modal.FooterForm />
					</>
				)}
			</Modal>
		</FormProvider>
	);
}
